/**
 * RT planning viewport — MVP.
 *
 * 3-pane MPR (axial / coronal / sagittal) rendered from the
 * `Volume` the 3-D ray-marcher already builds. Dose is sampled from
 * a simulated RTDOSE grid (see `./dose.ts`) and shown as an
 * isodose-threshold colourwash on each plane. A right-side DVH panel
 * plots cumulative dose-volume histograms for each ROI.
 *
 * This is the first iteration — enough structure to drive UI review
 * + dry-run the plan-review report template + screenshot the demo.
 * Real RTDOSE / RTSTRUCT parsing lands in a follow-up when an RT
 * study is ingested into the sandbox.
 *
 * Pattern sources: 3D Slicer's "RT Doses" panel, Varian Eclipse's
 * 3-pane planning layout, MIM Software's DVH view.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import type { Volume } from "@viewer/volume/types";
import { simulateDose, simulateRois, dvhCurve, vPct, type Roi } from "./dose";

export interface MPRViewportProps {
  volume: Volume;
}

type PlaneKind = "axial" | "coronal" | "sagittal";

export function MPRViewport(p: MPRViewportProps) {
  // --------------------------------------------------------------
  // Dose grid + ROIs are derived once per volume. The computation
  // is CPU-bound and not reactive during scrubbing, so memoising
  // on .seriesUid keeps Solid from rebuilding on every slider tick.
  // --------------------------------------------------------------
  const plan = createMemo(() => {
    const v = p.volume;
    const dose = simulateDose(v);
    const rois = simulateRois(v);
    return { volume: v, dose, rois };
  });

  const [wc, setWc] = createSignal(40);
  const [ww, setWw] = createSignal(400);
  const [doseOpacity, setDoseOpacity] = createSignal(0.4);
  const [showIsodose, setShowIsodose] = createSignal(true);
  const [axialIdx,    setAxialIdx]    = createSignal(0);
  const [coronalIdx,  setCoronalIdx]  = createSignal(0);
  const [sagittalIdx, setSagittalIdx] = createSignal(0);

  // Seed slice indices to the centre of each axis so the volume
  // isn't showing its edges at mount.
  createEffect(() => {
    const v = p.volume;
    setAxialIdx(Math.floor(v.depth  / 2));
    setCoronalIdx(Math.floor(v.height / 2));
    setSagittalIdx(Math.floor(v.width / 2));
  });

  // --------------------------------------------------------------
  // Plane rasterizer — writes directly into an RGBA ImageData; we
  // blend the dose colormap on top in the same pass so we don't
  // pay for an extra per-pixel read.
  // --------------------------------------------------------------
  function drawPlane(
    kind: PlaneKind, idx: number, ctx: CanvasRenderingContext2D,
    cw: number, ch: number,
  ) {
    const v = p.volume;
    const { dose } = plan();
    const ww_ = ww(), wc_ = wc();
    const lo = wc_ - ww_ / 2, hi = wc_ + ww_ / 2;
    let W: number, H: number;
    let sampler: (u: number, vv: number) => number;
    let doseSampler: (u: number, vv: number) => number;
    if (kind === "axial") {
      W = v.width; H = v.height;
      const base = idx * v.width * v.height;
      sampler     = (u, vv) => {
        const raw = v.data[base + vv * v.width + u];
        return (v.signed ? raw - 32768 : raw) * v.slope + v.intercept;
      };
      doseSampler = (u, vv) => dose.data[base + vv * v.width + u];
    } else if (kind === "coronal") {
      W = v.width; H = v.depth;
      sampler = (u, vv) => {
        const z = vv;
        const raw = v.data[z * v.width * v.height + idx * v.width + u];
        return (v.signed ? raw - 32768 : raw) * v.slope + v.intercept;
      };
      doseSampler = (u, vv) => {
        const z = vv;
        return dose.data[z * v.width * v.height + idx * v.width + u];
      };
    } else {
      // sagittal
      W = v.height; H = v.depth;
      sampler = (u, vv) => {
        const z = vv, y = u;
        const raw = v.data[z * v.width * v.height + y * v.width + idx];
        return (v.signed ? raw - 32768 : raw) * v.slope + v.intercept;
      };
      doseSampler = (u, vv) => {
        const z = vv, y = u;
        return dose.data[z * v.width * v.height + y * v.width + idx];
      };
    }
    const img = ctx.createImageData(cw, ch);
    const alpha = Math.round(doseOpacity() * 255);
    const isodose = showIsodose();
    for (let cy = 0; cy < ch; cy++) {
      for (let cx = 0; cx < cw; cx++) {
        const u = Math.floor(cx * W / cw);
        const vv = Math.floor(cy * H / ch);
        const huVal = sampler(u, vv);
        let t = (huVal - lo) / (hi - lo);
        if (t < 0) t = 0; else if (t > 1) t = 1;
        const g = Math.round(t * 255);
        const j = (cy * cw + cx) * 4;
        img.data[j]     = g;
        img.data[j + 1] = g;
        img.data[j + 2] = g;
        img.data[j + 3] = 255;
        if (alpha > 0 || isodose) {
          const d = doseSampler(u, vv);
          const df = d / Math.max(1, dose.max);
          if (df > 0.15) {
            // Jet-style dose colormap (cool → warm).
            const r = Math.min(255, Math.round(255 * Math.max(0, Math.min(1, df * 2))));
            const g2 = Math.min(255, Math.round(255 * Math.max(0, Math.min(1, 2 - Math.abs(df * 2 - 1) * 2))));
            const b = Math.min(255, Math.round(255 * Math.max(0, Math.min(1, 1 - df * 2))));
            // Front-to-back blend: C = a·Cd + (1-a)·Ci.
            const a = alpha / 255;
            img.data[j]     = Math.round(r  * a + img.data[j]     * (1 - a));
            img.data[j + 1] = Math.round(g2 * a + img.data[j + 1] * (1 - a));
            img.data[j + 2] = Math.round(b  * a + img.data[j + 2] * (1 - a));
          }
        }
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  // --------------------------------------------------------------
  // Canvas refs, redraw triggers on every reactive input change.
  // --------------------------------------------------------------
  let axC!: HTMLCanvasElement, coC!: HTMLCanvasElement, saC!: HTMLCanvasElement;

  function redrawAll() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      [axC, coC, saC].forEach(fitToCanvas);
      const axCtx = axC.getContext("2d"); if (axCtx) drawPlane("axial",    axialIdx(),    axCtx, axC.width, axC.height);
      const coCtx = coC.getContext("2d"); if (coCtx) drawPlane("coronal",  coronalIdx(),  coCtx, coC.width, coC.height);
      const saCtx = saC.getContext("2d"); if (saCtx) drawPlane("sagittal", sagittalIdx(), saCtx, saC.width, saC.height);
    });
  }
  let raf = 0;
  function fitToCanvas(c: HTMLCanvasElement) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(c.clientWidth  * dpr));
    const h = Math.max(1, Math.floor(c.clientHeight * dpr));
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
  }
  createEffect(() => {
    p.volume; plan(); axialIdx(); coronalIdx(); sagittalIdx();
    wc(); ww(); doseOpacity(); showIsodose();
    redrawAll();
  });
  onMount(() => {
    const ro = new ResizeObserver(() => redrawAll());
    [axC, coC, saC].forEach((c) => ro.observe(c));
    onCleanup(() => ro.disconnect());
    redrawAll();
  });

  // --------------------------------------------------------------
  // DVH — per-ROI cumulative curve, rendered in a small 2-D canvas.
  // --------------------------------------------------------------
  let dvhC!: HTMLCanvasElement;
  function drawDVH() {
    if (!dvhC) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = Math.max(1, Math.floor(dvhC.clientWidth  * dpr));
    const ch = Math.max(1, Math.floor(dvhC.clientHeight * dpr));
    if (dvhC.width !== cw || dvhC.height !== ch) { dvhC.width = cw; dvhC.height = ch; }
    const ctx = dvhC.getContext("2d"); if (!ctx) return;
    ctx.fillStyle = "#0b0f19";
    ctx.fillRect(0, 0, cw, ch);
    const { dose, rois } = plan();
    // Grid.
    ctx.strokeStyle = "#1f2937";
    ctx.lineWidth = 1;
    for (let g = 1; g < 5; g++) {
      const y = Math.round((g / 5) * ch);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cw, y); ctx.stroke();
    }
    for (let g = 1; g < 8; g++) {
      const x = Math.round((g / 8) * cw);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, ch); ctx.stroke();
    }
    // Curves.
    for (const roi of rois) {
      const curve = dvhCurve(dose, roi);
      ctx.strokeStyle = roi.color;
      ctx.lineWidth = 2 * dpr;
      ctx.beginPath();
      for (let i = 0; i < curve.length; i++) {
        const x = (curve[i].x / dose.max) * cw;
        const y = (1 - curve[i].y) * ch;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    // Axes labels.
    ctx.fillStyle = "#94a3b8";
    ctx.font = `${11 * dpr}px ui-monospace, monospace`;
    ctx.fillText(`0`, 4 * dpr, ch - 4 * dpr);
    ctx.fillText(`${dose.max.toFixed(0)} cGy`, cw - 72 * dpr, ch - 4 * dpr);
    ctx.fillText(`100%`, 4 * dpr, 14 * dpr);
  }
  createEffect(() => { plan(); drawDVH(); });
  onMount(() => {
    const ro = new ResizeObserver(drawDVH);
    ro.observe(dvhC);
    onCleanup(() => ro.disconnect());
    drawDVH();
  });

  // Dose metrics — V95%/V70Gy style summaries next to each ROI.
  function metrics(roi: Roi) {
    const { dose } = plan();
    const v95  = vPct(dose, roi, dose.max * 0.95);
    const v70  = vPct(dose, roi, 7000);     // 70 Gy = 7000 cGy
    const v50  = vPct(dose, roi, 5000);
    return { v95, v70, v50 };
  }

  return (
    <div class="relative flex h-full w-full flex-col bg-ink-0">
      {/* Top tool strip. */}
      <div class="flex flex-wrap items-center gap-2 border-b border-ink-300 bg-ink-100 px-3 py-1.5 text-[11px]">
        <span class="chip-brand !text-[9px]">RT PLAN</span>
        <span class="text-ink-700">W/L</span>
        <input type="number" value={wc()} class="w-[4.5rem] rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 font-mono text-[11px] text-ink-900 focus:border-brand focus:outline-none" onInput={(e) => setWc(parseFloat((e.currentTarget as HTMLInputElement).value))} title="Window centre (HU)" />
        <span class="text-ink-700">/</span>
        <input type="number" value={ww()} class="w-[4.5rem] rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 font-mono text-[11px] text-ink-900 focus:border-brand focus:outline-none" onInput={(e) => setWw(parseFloat((e.currentTarget as HTMLInputElement).value))} title="Window width (HU)" min="1" />
        <span class="ml-2 text-ink-700">dose α</span>
        <input type="range" min="0" max="1" step="0.01" value={doseOpacity()}
               class="h-1 w-24 accent-brand"
               onInput={(e) => setDoseOpacity(parseFloat((e.currentTarget as HTMLInputElement).value))} />
        <button type="button"
                class="rounded border border-ink-300 px-2 py-0.5 text-[10px] uppercase"
                classList={{ "bg-brand text-white border-brand": showIsodose(), "text-ink-700": !showIsodose() }}
                onClick={() => setShowIsodose((v) => !v)}>
          isodose
        </button>
        <span class="ml-auto text-[10px] text-ink-700">
          simulated dose + ROIs · real RTDOSE/RTSTRUCT parsing pending
        </span>
      </div>

      <div class="flex min-h-0 flex-1">
        {/* 3 MPR panes + DVH ------------------------------------- */}
        <div class="grid flex-1 grid-cols-2 grid-rows-2 gap-1 p-1">
          <Plane label="Axial"     canvas={(el) => (axC = el)} idx={axialIdx()} setIdx={setAxialIdx} max={p.volume.depth - 1} />
          <Plane label="Coronal"   canvas={(el) => (coC = el)} idx={coronalIdx()} setIdx={setCoronalIdx} max={p.volume.height - 1} />
          <Plane label="Sagittal"  canvas={(el) => (saC = el)} idx={sagittalIdx()} setIdx={setSagittalIdx} max={p.volume.width - 1} />
          <div class="relative rounded border border-ink-300 bg-black p-2">
            <div class="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-700">
              <span>DVH</span>
              <span class="text-ink-700">cumulative · cGy</span>
            </div>
            <canvas ref={dvhC} class="h-[calc(100%-1rem)] w-full" />
          </div>
        </div>

        {/* Right dock — structure set + metrics. */}
        <aside class="w-64 shrink-0 border-l border-ink-300 bg-ink-100">
          <div class="border-b border-ink-300 px-3 py-2 text-[10px] uppercase tracking-wider text-ink-700">
            Structure set
          </div>
          <ul class="divide-y divide-ink-300">
            <For each={plan().rois}>{(roi) => {
              const m = metrics(roi);
              return (
                <li class="px-3 py-2 text-[11px]">
                  <div class="flex items-center gap-2">
                    <span class="h-2.5 w-2.5 rounded-sm" style={{ "background-color": roi.color }} />
                    <span class="font-medium text-ink-1000">{roi.name}</span>
                    <span class="ml-auto font-mono text-ink-700">
                      {(roi.count / 1000).toFixed(0)} kvox
                    </span>
                  </div>
                  <div class="mt-1 grid grid-cols-3 gap-1 text-center text-[10px]">
                    <div class="rounded bg-ink-200 px-1 py-0.5">
                      V95% <span class="font-mono text-ink-900">{m.v95.toFixed(1)}%</span>
                    </div>
                    <div class="rounded bg-ink-200 px-1 py-0.5">
                      V70Gy <span class="font-mono text-ink-900">{m.v70.toFixed(1)}%</span>
                    </div>
                    <div class="rounded bg-ink-200 px-1 py-0.5">
                      V50Gy <span class="font-mono text-ink-900">{m.v50.toFixed(1)}%</span>
                    </div>
                  </div>
                </li>
              );
            }}</For>
          </ul>
        </aside>
      </div>
    </div>
  );
}

function Plane(p: {
  label:  string;
  canvas: (c: HTMLCanvasElement) => void;
  idx:    number;
  setIdx: (n: number) => void;
  max:    number;
}) {
  return (
    <div class="relative rounded border border-ink-300 bg-black">
      <div class="absolute left-2 top-1 z-10 text-[10px] uppercase tracking-wider text-white/70">
        {p.label}
      </div>
      <canvas ref={p.canvas} class="block h-full w-full" />
      <div class="absolute inset-x-2 bottom-1 flex items-center gap-2">
        <input type="range" min="0" max={p.max} step="1"
               value={p.idx}
               onInput={(e) => p.setIdx(parseInt((e.currentTarget as HTMLInputElement).value, 10))}
               class="h-1 flex-1 accent-brand" />
        <span class="font-mono text-[10px] text-white/80">
          {p.idx + 1}/{p.max + 1}
        </span>
      </div>
    </div>
  );
}
